// CR131: Hook for loading and saving global ops settings
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

/**
 * Batch execution defaults structure
 */
export interface BatchExecutionDefaults {
  auto_collection: boolean;
  auto_processing: boolean;
  auto_publish: boolean;
  timeout_seconds: number;
  max_concurrent_scans: number;
}

/**
 * Default values (matches seeded data)
 */
export const DEFAULT_BATCH_EXECUTION: BatchExecutionDefaults = {
  auto_collection: true,
  auto_processing: true,
  auto_publish: false,
  timeout_seconds: 65,
  max_concurrent_scans: 3,
};

interface UseOpsSettingsOptions {
  /** Auto-load on mount */
  autoLoad?: boolean;
  /** Settings key to load */
  key?: string;
}

interface OpsSettingsState {
  value: BatchExecutionDefaults;
  isDefault: boolean;
  updatedAt: string | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

export function useOpsSettings(options: UseOpsSettingsOptions = {}) {
  const { autoLoad = true, key = "batch.defaults.execution" } = options;
  const { toast } = useToast();

  const [state, setState] = useState<OpsSettingsState>({
    value: DEFAULT_BATCH_EXECUTION,
    isDefault: true,
    updatedAt: null,
    isLoading: autoLoad,
    isSaving: false,
    error: null,
  });

  /**
   * Load settings from database
   */
  const loadSettings = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Use direct fetch since supabase.functions.invoke doesn't support query params
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/ops-settings?key=${encodeURIComponent(key)}&scope=global`,
        {
          method: "GET",
          headers: {
            "apikey": anonKey,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load settings: ${response.status}`);
      }

      const result = await response.json();

      setState({
        value: result.value || DEFAULT_BATCH_EXECUTION,
        isDefault: result.is_default ?? true,
        updatedAt: result.updated_at,
        isLoading: false,
        isSaving: false,
        error: null,
      });

      return result.value || DEFAULT_BATCH_EXECUTION;
    } catch (err) {
      console.error("[useOpsSettings] Load error:", err);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to load settings",
      }));
      return DEFAULT_BATCH_EXECUTION;
    }
  }, [key]);

  /**
   * Save settings to database (admin only)
   */
  const saveSettings = useCallback(async (
    newValue: Partial<BatchExecutionDefaults>,
    showToast = true
  ) => {
    const merged = { ...state.value, ...newValue };
    setState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Authentication required");
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/ops-settings`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          scope: "global",
          value_json: merged,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to save: ${response.status}`);
      }

      const result = await response.json();

      setState({
        value: result.value,
        isDefault: false,
        updatedAt: result.updated_at,
        isLoading: false,
        isSaving: false,
        error: null,
      });

      if (showToast) {
        toast({
          title: "Settings Saved",
          description: "Default execution settings updated for future runs",
        });
      }

      return true;
    } catch (err) {
      console.error("[useOpsSettings] Save error:", err);
      const message = err instanceof Error ? err.message : "Failed to save settings";
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: message,
      }));

      if (showToast) {
        toast({
          title: "Error",
          description: message,
          variant: "destructive",
        });
      }

      return false;
    }
  }, [key, state.value, toast]);

  /**
   * Update a single field and save
   */
  const updateField = useCallback(async <K extends keyof BatchExecutionDefaults>(
    field: K,
    value: BatchExecutionDefaults[K],
    showToast = true
  ) => {
    return saveSettings({ [field]: value }, showToast);
  }, [saveSettings]);

  /**
   * Reset to defaults
   */
  const resetToDefaults = useCallback(async () => {
    return saveSettings(DEFAULT_BATCH_EXECUTION, true);
  }, [saveSettings]);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad) {
      loadSettings();
    }
  }, [autoLoad, loadSettings]);

  return {
    ...state,
    loadSettings,
    saveSettings,
    updateField,
    resetToDefaults,
  };
}
